import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Search,
  Filter,
  Download,
  Eye,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Package,
  Users,
  TrendingUp,
  MoreHorizontal
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { StatsCard } from '../components/ui/stats-card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { OrderAPI } from '../lib/api';
import { Order, OrderStatus } from '../lib/types';
import {
  formatCurrency,
  formatDateShort,
  formatTime,
  getOrderStatusBadgeVariant,
  formatOrderStatus,
  truncateText
} from '../lib/utils';
import OrderDetailModal from '../components/orders/OrderDetailModal';
import MobileOrderCard from '../components/orders/MobileOrderCard';
import { StatusBadge } from '../components/ui/status-badge';

// Order status options for filter
type OrderStatusId = 'all' | OrderStatus;

// Define order status display options
const orderStatuses: Array<{ id: OrderStatusId; name: string; icon: React.ReactNode; color: string }> = [
  { id: 'all', name: 'All Orders', icon: <Package className="h-5 w-5" />, color: 'text-gray-500' },
  { id: 'pending', name: 'Pending', icon: <Clock className="h-5 w-5" />, color: 'text-yellow-500' },
  { id: 'confirmed', name: 'Confirmed', icon: <CheckCircle className="h-5 w-5" />, color: 'text-blue-500' },
  { id: 'processing', name: 'Processing', icon: <Truck className="h-5 w-5" />, color: 'text-blue-500' },
  { id: 'ready_for_pickup', name: 'Ready for Pickup', icon: <Truck className="h-5 w-5" />, color: 'text-purple-500' },
  { id: 'picked_up', name: 'Picked Up', icon: <Truck className="h-5 w-5" />, color: 'text-purple-500' },
  { id: 'in_transit', name: 'In Transit', icon: <Truck className="h-5 w-5" />, color: 'text-purple-500' },
  { id: 'issue_reported', name: 'Issue Reported', icon: <AlertCircle className="h-5 w-5" />, color: 'text-orange-500' },
  { id: 'delivered', name: 'Delivered', icon: <CheckCircle className="h-5 w-5" />, color: 'text-green-500' },
  { id: 'completed', name: 'Completed', icon: <CheckCircle className="h-5 w-5" />, color: 'text-green-500' },
  { id: 'cancelled', name: 'Cancelled', icon: <XCircle className="h-5 w-5" />, color: 'text-red-500' },
  { id: 'rejected', name: 'Rejected', icon: <XCircle className="h-5 w-5" />, color: 'text-red-500' },
  { id: 'refunded', name: 'Refunded', icon: <XCircle className="h-5 w-5" />, color: 'text-red-500' },
];

// Loading skeleton component for table rows
const TableRowSkeleton = () => (
  <TableRow className="enhanced-table-row">
    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
    <TableCell>
      <div className="space-y-1">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-24" />
      </div>
    </TableCell>
    <TableCell>
      <div className="space-y-1">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-3 w-16" />
      </div>
    </TableCell>
    <TableCell><Skeleton className="h-4 w-8" /></TableCell>
    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
    <TableCell><Skeleton className="h-6 w-16 rounded-full" /></TableCell>
    <TableCell>
      <div className="space-y-1">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-20" />
      </div>
    </TableCell>
  </TableRow>
);

export default function OrdersPage({ initialStatus = 'all' }: { initialStatus?: OrderStatusId }) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<OrderStatusId>(initialStatus);
  const [orderCounts, setOrderCounts] = useState<Record<OrderStatusId, number>>({
    all: 0,
    pending: 0,
    confirmed: 0,
    processing: 0,
    ready_for_pickup: 0,
    picked_up: 0,
    in_transit: 0,
    issue_reported: 0,
    delivered: 0,
    completed: 0,
    cancelled: 0,
    rejected: 0,
    refunded: 0,
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Order details modal
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Fetch orders from API
  useEffect(() => {
    fetchOrders();
    fetchOrderStats();
  }, [selectedStatus, currentPage, pageSize]);

  // Update selected status when initialStatus prop changes
  useEffect(() => {
    setSelectedStatus(initialStatus);
  }, [initialStatus]);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams: any = {
        page: currentPage,
        limit: pageSize,
        sort: 'createdAt',
        order: 'desc',
      };

      // Add status filter if not "all"
      if (selectedStatus !== 'all') {
        queryParams.status = selectedStatus;
      }

      // Add search if provided
      if (searchQuery.trim()) {
        queryParams.search = searchQuery.trim();
      }

      const response = await OrderAPI.getAllOrders(queryParams);

      setOrders(response.data.orders);
      setTotalPages(response.data.pagination.pages);
      setTotalOrders(response.data.pagination.total);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderStats = async () => {
    try {
      const response = await OrderAPI.getStatistics();

      // Create the counts object
      const counts: Record<OrderStatusId, number> = {
        all: response.data.totalOrders,
        ...response.data.statusCounts as Record<OrderStatus, number>
      };

      setOrderCounts(counts);
    } catch (err) {
      console.error('Failed to fetch order statistics:', err);
    }
  };

  // Handle status filter click
  const handleStatusFilterClick = (statusId: OrderStatusId) => {
    setSelectedStatus(statusId);
    setCurrentPage(1); // Reset to first page when changing filters
  };

  // Handle search
  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when searching
    fetchOrders();
  };

  // Handle key press in search input
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle pagination
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Open order detail modal
  const openOrderDetail = async (orderId: string) => {
    try {
      const response = await OrderAPI.getOrder(orderId);
      if (response.success && response.data) {
        setSelectedOrder(response.data);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    }
  };

  // Handle order update
  const handleOrderUpdated = () => {
    // Refresh the list and stats when an order is updated
    fetchOrders();
    fetchOrderStats();
  };

  // Get assignment status display
  const getAssignmentStatus = (order: Order) => {
    const hasSupplier = order.assignedSupplier || order.supplier ||
      (order.items && order.items.some(item => item.assignedSupplier || item.supplier || item.supplierName));

    const hasTransporter = order.transporter;

    const needsTransporter = ['confirmed', 'processing', 'ready_for_pickup'].includes(order.status);

    return {
      supplier: hasSupplier ? 'assigned' : 'pending',
      transporter: hasTransporter ? 'assigned' : (needsTransporter ? 'pending' : 'not_needed')
    };
  };

  // Get key metrics for the top cards
  const keyMetrics = [
    {
      title: 'Total Orders',
      value: orderCounts.all,
      icon: <Package className="h-6 w-6" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Pending Orders',
      value: orderCounts.pending,
      icon: <Clock className="h-6 w-6" />,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
    },
    {
      title: 'In Progress',
      value: orderCounts.processing + orderCounts.confirmed + orderCounts.ready_for_pickup + orderCounts.picked_up + orderCounts.in_transit,
      icon: <TrendingUp className="h-6 w-6" />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Completed',
      value: orderCounts.completed + orderCounts.delivered,
      icon: <CheckCircle className="h-6 w-6" />,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-8 px-1 sm:px-0">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Order Management</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1 sm:mt-2">Monitor and manage customer orders efficiently</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
            <Filter className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Advanced </span>Filter
          </Button>
          <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {keyMetrics.map((metric, index) => (
          <StatsCard
            key={index}
            title={metric.title}
            value={metric.value}
            icon={metric.icon}
            iconClassName={`${metric.bgColor} ${metric.color}`}
          />
        ))}
      </div>

      {/* Search and Actions */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by order #, customer email..."
                className="pl-10 w-full sm:w-[350px]"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
              />
            </div>
            <Button onClick={handleSearch} className="btn-primary-enhanced">
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Status Filter Pills - Scrollable on mobile */}
      <div className="mobile-filter-bar sm:flex sm:flex-wrap sm:gap-3 sm:mx-0 sm:px-0">
        {orderStatuses.map((status) => (
          <Button
            key={status.id}
            variant={selectedStatus === status.id ? "default" : "outline"}
            size="sm"
            onClick={() => handleStatusFilterClick(status.id)}
            className={`mobile-filter-pill flex items-center text-xs sm:text-sm transition-all ${selectedStatus === status.id
              ? 'shadow-md scale-105'
              : 'hover:scale-105 hover:shadow-sm'
              }`}
          >
            <span className={`mr-1.5 sm:mr-2 ${selectedStatus === status.id ? 'text-primary-foreground' : status.color}`}>
              {status.icon}
            </span>
            <span className="whitespace-nowrap">{status.name}</span>
            {status.id !== 'all' && orderCounts[status.id] > 0 && (
              <Badge
                variant="secondary"
                className="ml-1.5 sm:ml-2 bg-primary-foreground text-primary text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5"
              >
                {orderCounts[status.id]}
              </Badge>
            )}
          </Button>
        ))}
      </div>

      {/* Error Display */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="p-4">
            <div className="flex items-center text-destructive">
              <AlertCircle className="h-4 w-4 mr-2" />
              <span className="font-medium">Error: </span>
              <span className="ml-1">{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orders Table */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center justify-between text-base sm:text-lg">
            <span>Orders</span>
            <span className="text-xs sm:text-sm font-normal text-muted-foreground">
              {totalOrders} total
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Mobile View - Card List */}
          <div className="block lg:hidden p-3 sm:p-4">
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="mobile-data-card animate-pulse">
                    <div className="h-16 bg-muted rounded" />
                  </div>
                ))}
              </div>
            ) : (orders && orders.length === 0) ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Package className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-base font-medium">No orders found</p>
                <p className="text-sm text-center px-4">
                  {searchQuery || selectedStatus !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Orders will appear here once placed'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((order) => (
                  <MobileOrderCard
                    key={order._id}
                    order={order}
                    onViewDetails={openOrderDetail}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Desktop View - Table */}
          <div className="hidden lg:block rounded-md border-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b">
                    <TableHead className="font-semibold">Order ID</TableHead>
                    <TableHead className="font-semibold">Customer</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Items</TableHead>
                    <TableHead className="font-semibold">Amount</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Assignment</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    // Loading skeleton rows
                    Array.from({ length: pageSize }).map((_, index) => (
                      <TableRowSkeleton key={index} />
                    ))
                  ) : (orders && orders.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <div className="flex flex-col items-center justify-center text-muted-foreground">
                          <Package className="h-12 w-12 mb-4 opacity-50" />
                          <p className="text-lg font-medium">No orders found</p>
                          <p className="text-sm">
                            {searchQuery || selectedStatus !== 'all'
                              ? 'Try adjusting your filters or search terms'
                              : 'Orders will appear here once customers place them'}
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order) => {
                      const assignment = getAssignmentStatus(order);
                      return (
                        <TableRow
                          key={order._id}
                          className="enhanced-table-row cursor-pointer"
                          onClick={() => openOrderDetail(order._id)}
                        >
                          <TableCell className="font-medium">
                            <div className="flex flex-col">
                              <span className="font-semibold text-primary">
                                {order.orderNumber}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {order.trader?.firstName
                                  ? `${order.trader.firstName} ${order.trader.lastName || ''}`
                                  : order.trader?.email || 'N/A'}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {order.trader?.firstName ? order.trader?.email : (order.trader?.mobile || '')}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">{formatDateShort(order.createdAt)}</span>
                              <span className="text-xs text-muted-foreground">
                                {formatTime(order.createdAt)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-medium">
                              {order.itemsCount ?? order.items?.length ?? 0} items
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="font-semibold text-lg">
                              {formatCurrency(order.totalAmount)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={order.status} />
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center text-xs">
                                <Users className="h-3 w-3 mr-1" />
                                <span className="font-medium">Supplier:</span>
                                <Badge
                                  variant={assignment.supplier === 'assigned' ? 'success' : 'warning'}
                                  className="ml-1 text-xs"
                                >
                                  {assignment.supplier === 'assigned' ? 'Assigned' : 'Pending'}
                                </Badge>
                              </div>
                              {assignment.transporter !== 'not_needed' && (
                                <div className="flex items-center text-xs">
                                  <Truck className="h-3 w-3 mr-1" />
                                  <span className="font-medium">Transport:</span>
                                  <Badge
                                    variant={assignment.transporter === 'assigned' ? 'success' : 'warning'}
                                    className="ml-1 text-xs"
                                  >
                                    {assignment.transporter === 'assigned' ? 'Assigned' : 'Pending'}
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Pagination */}
      {!loading && orders.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * pageSize, totalOrders)}</span> of{' '}
                <span className="font-medium">{totalOrders}</span> orders
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => goToPage(page)}
                        className="h-8 w-8 p-0"
                      >
                        {page}
                      </Button>
                    );
                  })}
                  {totalPages > 5 && (
                    <>
                      <span className="text-muted-foreground">...</span>
                      <Button
                        variant={currentPage === totalPages ? "default" : "outline"}
                        size="sm"
                        onClick={() => goToPage(totalPages)}
                        className="h-8 w-8 p-0"
                      >
                        {totalPages}
                      </Button>
                    </>
                  )}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Order Detail Modal */}
      <OrderDetailModal
        isOpen={!!selectedOrder}
        onClose={() => {
          setSelectedOrder(null);
          // Refresh orders list after closing modal
          fetchOrders();
        }}
        order={selectedOrder}
        onOrderUpdated={() => {
          // Refresh orders list and stats when order is updated
          fetchOrders();
          fetchOrderStats();
        }}
      />
    </div>
  );
} 